import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { ConfigServiceValue } from "@fabric-types/svc-config";
import { ExposeObserver } from "@obg/event-bus/types";
import { DocumentRef, PlatformService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { Observable, of } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { OPTIMIZELY_FEATURE_FLAGS } from "../const";

import { FeatureFlag, OptimizelyFeatureFlags, OptimizelyFeatureFlagsConfig } from "./feature-flag.model";

@Injectable({
	providedIn: "root",
})
export class FeatureFlagService {
	private _defaults: Record<string, unknown> | undefined;

	private _featureFlags$: ExposeObserver<Record<string, any>> | undefined;

	constructor(
		private httpClient: HttpClient,
		private platformService: PlatformService,
		private documentRef: DocumentRef,
		@Inject(OPTIMIZELY_FEATURE_FLAGS) private optimizelyFeatureFlags: OptimizelyFeatureFlags
	) {
		if (!this.documentRef?.obgEventBus) {
			return;
		}

		this._featureFlags$ = this.documentRef.obgEventBus.expose(ConfigServiceValue.FeatureFlags);

		this.populateEventBusFeatureFlags$();
	}

	/**
	 * The following function returns with the selected feature flag value by the key parameter or 'undefined'
	 * @param key @type {string}
	 * @returns {FeatureFlag<T> | undefined}
	 */
	get<T>(key: string): FeatureFlag<T> | undefined {
		return this.optimizelyFeatureFlags.featureFlags[key];
	}

	/**
	 * The following function returns with an Observable with selected feature flag value by the key parameter or 'undefined'
	 * The config contains the following options:
	 * - ***defaultValue***: it is possible to add default value to the stream when the feature flag object is 'undefined' (it can be 'undefined' during the SSR)
	 * Warning: it can cause flaky rendering in some cases
	 * - ***isEnabledOnlyClientSide***: if the following flag is 'true' the stream will only emit value in client side
	 * @param key @type {string}
	 * @param config @type {OptimizelyFeatureFlagsConfig<T>}
	 * @returns {Observable<FeatureFlag<T> | undefined>}
	 */
	get$<T>(key: string, config?: OptimizelyFeatureFlagsConfig<T>): Observable<FeatureFlag<T> | undefined> {
		if (!config) {
			return this.optimizelyFeatureFlags.featureFlags$.pipe(
				ignoreNil(),
				map((featureFlags) => featureFlags[key])
			);
		}

		const { defaultValue, isEnabledOnlyClientSide } = config;

		if (defaultValue) {
			this._defaults = { ...(this._defaults ?? {}), ...{ [key]: defaultValue } };
		}

		return this.optimizelyFeatureFlags.featureFlags$.pipe(
			filter(() => (isEnabledOnlyClientSide ? this.platformService.isBrowser : true)),
			switchMap((featureFlags) => (featureFlags ? of(featureFlags[key] ?? defaultValue) : of(defaultValue)))
		);
	}

	trackEvent = (eventName: string, eventTags: Record<string, any>): Observable<any> => {
		return this.httpClient.post("/track-optimizely-event", {
			eventName,
			eventTags,
		});
	};

	private populateEventBusFeatureFlags$() {
		if (!this._featureFlags$) {
			return;
		}

		this.optimizelyFeatureFlags.featureFlags$
			.pipe(
				take(1),
				map((featureFlags) => {
					if (!this._defaults) {
						return featureFlags;
					}

					const clone = { ...featureFlags };

					Object.entries(this._defaults).forEach(([key, value]) => {
						if (clone[key]) {
							return;
						}

						clone[key] = value;
					});

					return clone;
				}),
				tap((featureFlags) => {
					this._featureFlags$!.next(featureFlags);
				})
			)
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}
}
